import { useState } from "react";
import { Upload, Trash2, ExternalLink } from "lucide-react";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";

/**
 * ImageField — paste a URL or upload from device. Returns a relative path
 * (e.g. "/api/images/<id>") suitable for storage in DB. Renders a live preview.
 */
export default function ImageField({ value, onChange, label, testid, thumbnail }) {
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (file.size > 15 * 1024 * 1024) { toast.error("Image must be ≤ 15 MB"); return; }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const endpoint = thumbnail ? "/upload/image/thumbnail" : "/upload/image";
        const { data } = await http.post(endpoint, { data_uri: reader.result, filename: file.name });
        onChange?.(data.url);
        toast.success("Image uploaded.");
      } catch {
        toast.error("Upload failed.");
      } finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div data-testid={`image-field-${testid || ""}`}>
      {label && <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">{label}</div>}
      <div className="flex gap-2 items-stretch">
        <input
          className="stc-input flex-1 font-mono text-[12px]"
          placeholder="Paste image URL or upload →"
          value={value || ""}
          onChange={e => onChange?.(e.target.value)}
          data-testid={`img-input-${testid || ""}`}
        />
        <label className={`btn-outline text-xs cursor-pointer flex items-center ${busy ? "opacity-50" : ""}`} data-testid={`img-upload-${testid || ""}`}>
          <Upload size={12}/> {busy ? "…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0])} disabled={busy}/>
        </label>
        {value && (
          <button type="button" onClick={() => onChange?.("")} className="btn-outline text-xs border-red-700 text-red-400" aria-label="Clear" data-testid={`img-clear-${testid || ""}`}><Trash2 size={12}/></button>
        )}
      </div>
      {value && (
        <div className="mt-2 relative inline-block">
          <img src={resolveImage(value)} alt="" className="max-h-32 border border-white/10"/>
          <a href={resolveImage(value)} target="_blank" rel="noreferrer" className="absolute top-1 right-1 bg-black/70 text-[#F4F1EA] p-1" aria-label="Open original"><ExternalLink size={10}/></a>
        </div>
      )}
    </div>
  );
}
